import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { CopyFieldMappingsDialog } from './copy-field-mappings-dialog';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useUpdateRelativityFieldMapping } from '@/api/mutations/useUpdateRelativityFieldMapping';
import useGetConfiguredRelativityWorkspaces from '@/api/queries/useGetConfiguredRelativityWorkspaces';
import useGetRelativityFieldChoices from '@/api/queries/useGetRelativityFieldChoices';
import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import useGetRelativityWorkspaceFields from '@/api/queries/useGetRelativityWorkspaceFields';
import type {
  FieldMappingResponse,
  FieldMappings,
  RelativityFieldType,
  RelativityWorkspaceType,
  TypeValidationResult,
} from '@/api/types/backend-dto-types';
import { validateTypeCompatibility, TypeCompatibility } from '@/api/types/backend-dto-types';
import { useHasPermission } from '@/hooks/useHasPermission';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/new/components/shadcn/command';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/new/components/shadcn/empty';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { useRelativityWorkspaceSearch } from '@/new/hooks/useRelativityWorkspaceSearch';
import { cn } from '@/new/lib/utils';

// Decode HTML entities like &#47; -> /
function decodeHtmlEntities(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || '';
}

// Auto-generate camelCase key from field name
// Handles special chars like "Sort Date/Time" -> "sortDateTime"
export function generateFieldKey(fieldName: string): string {
  return decodeHtmlEntities(fieldName)
    .replace(/[^a-zA-Z0-9\s]/g, ' ') // Replace special chars with spaces (so "Date/Time" becomes "Date Time")
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');
}

function normaliseFieldName(name: string): string {
  return decodeHtmlEntities(name).trim().toLowerCase();
}

export interface CopyMappingsResult {
  mappings: FieldMappings;
  parentIdentifierFieldKey: string | null;
  matchedCount: number;
  skipped: { name: string; reason: string }[];
}

// Build a set of field mappings for the target workspace by matching a source workspace's
// mappings onto the target's live fields.
export function buildCopiedMappings(
  sourceMappings: FieldMappings,
  sourceParentIdentifierFieldKey: string | null,
  targetFields: RelativityFieldType[],
): CopyMappingsResult {
  const targetByName = new Map<string, RelativityFieldType>();
  targetFields.forEach((field) => targetByName.set(normaliseFieldName(field.name), field));

  const mappings: FieldMappings = {};
  const skipped: { name: string; reason: string }[] = [];

  Object.values(sourceMappings).forEach((config) => {
    if (!config.enabled) return;

    const target = targetByName.get(normaliseFieldName(config.relativityFieldName));

    if (!target) {
      skipped.push({ name: config.relativityFieldName, reason: 'Not found in this workspace' });
      return;
    }

    if ((target.fieldType ?? '') !== config.relativityFieldType) {
      skipped.push({
        name: config.relativityFieldName,
        reason: `Type differs (${config.relativityFieldType} → ${target.fieldType ?? 'unknown'})`,
      });
      return;
    }

    mappings[target.artifactId.toString()] = {
      relativityFieldName: target.name,
      relativityFieldType: target.fieldType ?? '',
      fieldKey: generateFieldKey(target.name),
      displayName: config.displayName,
      enabled: true,
      ...(config.isArray !== undefined ? { isArray: config.isArray } : {}),
      ...(config.arraySeparator !== undefined ? { arraySeparator: config.arraySeparator } : {}),
    };
  });

  // Carry the parent identifier only if the field it referenced was copied across. fieldKey is
  // name-derived, so a matched field keeps the same fieldKey between workspaces.
  let parentIdentifierFieldKey: string | null = null;
  if (sourceParentIdentifierFieldKey !== null) {
    const carried = Object.values(mappings).some((m) => m.fieldKey === sourceParentIdentifierFieldKey);
    if (carried) {
      parentIdentifierFieldKey = sourceParentIdentifierFieldKey;
    } else {
      const sourceEntry = Object.values(sourceMappings).find((m) => m.fieldKey === sourceParentIdentifierFieldKey);
      skipped.push({
        name: sourceEntry?.relativityFieldName ?? sourceParentIdentifierFieldKey,
        reason: 'Parent identifier field not copied',
      });
    }
  }

  return { mappings, parentIdentifierFieldKey, matchedCount: Object.keys(mappings).length, skipped };
}

// Check if "Is Array" checkbox should be shown (Long Text fields only)
function shouldShowIsArrayCheckbox(field: RelativityFieldType | null): boolean {
  if (!field) return false;
  return field.fieldType === 'Long Text';
}

// Check if "Parent Control Number" switch should be shown.
// Hidden for the Identifier field itself (self-reference is meaningless)
// and for non-text field types (identifiers are strings in Relativity).
function shouldShowParentIdentifierSwitch(field: RelativityFieldType | null): boolean {
  if (!field) return false;
  if (field.isIdentifier === true) return false;
  return field.fieldType === 'Fixed-Length Text' || field.fieldType === 'Long Text';
}

// Check if field should be treated as array
function isEffectivelyArray(mapping: FieldMappings[string] | undefined): boolean {
  if (!mapping) return false;
  if (mapping.isArray !== undefined) return mapping.isArray;
  return mapping.arraySeparator !== undefined && mapping.arraySeparator !== '';
}

// Check if array separator input should be shown (only for Long Text marked as array)
function shouldShowArraySeparator(
  field: RelativityFieldType | null,
  mapping: FieldMappings[string] | undefined,
): boolean {
  if (!field || !mapping) return false;
  // Only show for Long Text when marked as array
  return field.fieldType === 'Long Text' && isEffectivelyArray(mapping);
}

export function FieldMappings() {
  const { hasPermission } = useHasPermission();
  const { isAuthenticated, isLoading: isAuthLoading, isConnecting, connectRelativity } = useRelativityAuth();
  const [selectedWorkspace, setSelectedWorkspace] = useState<RelativityWorkspaceType | null>(null);
  const [selectedField, setSelectedField] = useState<RelativityFieldType | null>(null);
  const [isWorkspacePickerOpen, setIsWorkspacePickerOpen] = useState(false);
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [showMappedOnly, setShowMappedOnly] = useState(false);
  const [localMappings, setLocalMappings] = useState<FieldMappings>({});
  const [localParentIdentifierFieldKey, setLocalParentIdentifierFieldKey] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);

  // Type validation state
  const [fieldValidations, setFieldValidations] = useState<Record<string, TypeValidationResult>>({});

  const {
    workspaces,
    searchQuery: workspaceSearchQuery,
    setSearchQuery: setWorkspaceSearchQuery,
    currentPage: workspaceCurrentPage,
    totalPages: workspaceTotalPages,
    canPrevious: canPreviousWorkspacePage,
    canNext: canNextWorkspacePage,
    goToPreviousPage: goToPreviousWorkspacePage,
    goToNextPage: goToNextWorkspacePage,
    isLoading: isLoadingWorkspaces,
    isFetching: isFetchingWorkspaces,
    error: workspaceError,
    reset: resetWorkspaceSearch,
  } = useRelativityWorkspaceSearch({
    enabled: isAuthenticated,
    pageSize: 8,
  });

  const { data: fields, isLoading: fieldsLoading } = useGetRelativityWorkspaceFields({
    workspaceId: selectedWorkspace?.artifactId || null,
    enabled: !!selectedWorkspace && isAuthenticated,
  });

  const { data: existingMapping } = useGetRelativityFieldMapping({
    workspaceId: selectedWorkspace?.artifactId || null,
    enabled: !!selectedWorkspace && isAuthenticated,
  });

  // Configured workspaces that can be used as copy sources (current workspace excluded).
  const { data: configuredWorkspaces } = useGetConfiguredRelativityWorkspaces({
    enabled: isAuthenticated,
  });

  const copySourceWorkspaces = useMemo(
    () =>
      (configuredWorkspaces ?? []).filter(
        // Exclude the current workspace and load-file-import workspaces (negative artifact IDs).
        (w) => w.workspaceId !== selectedWorkspace?.artifactId && w.workspaceId > 0,
      ),
    [configuredWorkspaces, selectedWorkspace],
  );

  // Fetch choices on-demand for selected choice fields
  const isChoiceField = selectedField?.fieldType === 'Single Choice' || selectedField?.fieldType === 'Multiple Choice';

  const { data: fieldChoicesData, isLoading: isLoadingChoices } = useGetRelativityFieldChoices({
    workspaceId: selectedWorkspace?.artifactId || null,
    fieldArtifactId: selectedField?.artifactId || null,
    enabled: !!selectedWorkspace && !!selectedField && isChoiceField && isAuthenticated,
  });

  const updateMappingMutation = useUpdateRelativityFieldMapping();

  // Initialize local mappings when existing mapping loads
  useEffect(() => {
    if (existingMapping) {
      setLocalMappings(existingMapping.fieldMappings);
      setLocalParentIdentifierFieldKey(existingMapping.parentIdentifierFieldKey);
      // Validate all existing mappings
      const validations: Record<string, TypeValidationResult> = {};
      Object.entries(existingMapping.fieldMappings).forEach(([artifactId, config]) => {
        validations[artifactId] = validateTypeCompatibility(config.relativityFieldType);
      });
      setFieldValidations(validations);
      setHasUnsavedChanges(false);
    } else if (selectedWorkspace) {
      setLocalMappings({});
      setLocalParentIdentifierFieldKey(null);
      setFieldValidations({});
      setHasUnsavedChanges(false);
    }
  }, [existingMapping, selectedWorkspace]);

  // Filtered fields based on search and mapping status
  const filteredFields = useMemo(() => {
    if (!fields) return [];

    let result = fields.fields;

    // Filter by search query
    if (fieldSearchQuery.trim()) {
      result = result.filter((field) => field.name.toLowerCase().includes(fieldSearchQuery.toLowerCase()));
    }

    // Filter by mapped status
    if (showMappedOnly) {
      result = result.filter((field) => !!localMappings[field.artifactId.toString()]);
    }

    return result;
  }, [fields, fieldSearchQuery, showMappedOnly, localMappings]);

  // Check if there are any validation errors (blocks save)
  const hasValidationErrors = useMemo(() => {
    return Object.values(fieldValidations).some((validation) => validation.compatible === TypeCompatibility.Error);
  }, [fieldValidations]);

  // Count errors
  const errorCount = useMemo(() => {
    return Object.values(fieldValidations).filter((v) => v.compatible === TypeCompatibility.Error).length;
  }, [fieldValidations]);

  // Enable field mapping with auto-generated fieldKey
  const enableFieldMapping = (artifactId: string, field: RelativityFieldType) => {
    const fieldKey = generateFieldKey(field.name);
    const displayName = decodeHtmlEntities(field.name);

    // Validate type compatibility (only blocks Object fields)
    const validation = validateTypeCompatibility(field.fieldType || '');

    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        relativityFieldName: field.name,
        relativityFieldType: field.fieldType || '',
        fieldKey,
        displayName,
        enabled: true,
      },
    }));

    setFieldValidations((prev) => ({
      ...prev,
      [artifactId]: validation,
    }));

    setHasUnsavedChanges(true);
  };

  // Copy mappings from another configured workspace, matching by field name/type onto the
  // fields of the currently-selected workspace. Overlays onto any existing edits; the user
  // reviews and saves through the normal flow (which re-fetches choice options and validates).
  const handleCopyMappings = (sourceMapping: FieldMappingResponse) => {
    if (!fields) return;

    const result = buildCopiedMappings(
      sourceMapping.fieldMappings,
      sourceMapping.parentIdentifierFieldKey,
      fields.fields,
    );

    if (result.matchedCount === 0) {
      toast.error('No fields could be copied — none matched a field in this workspace.');
      return;
    }

    setLocalMappings((prev) => {
      const merged = { ...prev, ...result.mappings };
      const validations: Record<string, TypeValidationResult> = {};
      Object.entries(merged).forEach(([artifactId, config]) => {
        validations[artifactId] = validateTypeCompatibility(config.relativityFieldType);
      });
      setFieldValidations(validations);
      return merged;
    });

    if (result.parentIdentifierFieldKey !== null) {
      setLocalParentIdentifierFieldKey(result.parentIdentifierFieldKey);
    }

    setHasUnsavedChanges(true);

    const sourceName = sourceMapping.workspaceName ?? `workspace ${sourceMapping.workspaceId}`;
    const copied = `Copied ${result.matchedCount} field${result.matchedCount === 1 ? '' : 's'} from ${sourceName}.`;
    toast.success(result.skipped.length === 0 ? copied : `${copied} ${result.skipped.length} skipped.`);
  };

  // Update display name
  const updateDisplayName = (artifactId: string, displayName: string) => {
    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        ...prev[artifactId],
        displayName,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Update array separator
  const updateArraySeparator = (artifactId: string, separator: string) => {
    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        ...prev[artifactId],
        arraySeparator: separator,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Update isArray setting for Long Text fields
  const updateIsArray = (artifactId: string, isArray: boolean) => {
    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        ...prev[artifactId],
        isArray,
        arraySeparator: isArray ? prev[artifactId]?.arraySeparator || ';' : prev[artifactId]?.arraySeparator,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Clear field mapping
  const clearFieldMapping = (artifactId: string) => {
    const cleared = localMappings[artifactId];

    // If this field was the parent identifier, clear that too — disabled fields
    // aren't stored, so they can't act as parent identifiers.
    if (cleared && localParentIdentifierFieldKey === cleared.fieldKey) {
      setLocalParentIdentifierFieldKey(null);
    }

    setLocalMappings((prev) => {
      const newMappings = { ...prev };
      delete newMappings[artifactId];
      return newMappings;
    });
    setFieldValidations((prev) => {
      const newValidations = { ...prev };
      delete newValidations[artifactId];
      return newValidations;
    });
    setHasUnsavedChanges(true);
  };

  // Save mappings
  const handleSave = async () => {
    if (!selectedWorkspace) return;

    // Coerce any cleared array separators back to the default ';' so we never
    // persist an empty separator (which would split values character-by-character).
    const normalizedMappings = Object.fromEntries(
      Object.entries(localMappings).map(([artifactId, mapping]) => [
        artifactId,
        mapping.arraySeparator === '' ? { ...mapping, arraySeparator: ';' } : mapping,
      ]),
    );

    try {
      await updateMappingMutation.mutateAsync({
        workspaceId: selectedWorkspace.artifactId,
        workspaceName: selectedWorkspace.name,
        fieldMappings: normalizedMappings,
        parentIdentifierFieldKey: localParentIdentifierFieldKey,
      });
      toast.success('Field mappings saved successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving field mappings:', error);
      toast.error('Failed to save field mappings');
    }
  };

  // Discard changes
  const handleDiscard = () => {
    if (existingMapping) {
      setLocalMappings(existingMapping.fieldMappings);
      setLocalParentIdentifierFieldKey(existingMapping.parentIdentifierFieldKey);
      const validations: Record<string, TypeValidationResult> = {};
      Object.entries(existingMapping.fieldMappings).forEach(([artifactId, config]) => {
        validations[artifactId] = validateTypeCompatibility(config.relativityFieldType);
      });
      setFieldValidations(validations);
    } else {
      setLocalMappings({});
      setLocalParentIdentifierFieldKey(null);
      setFieldValidations({});
    }
    setHasUnsavedChanges(false);
    toast.info('Changes discarded');
  };

  if (!hasPermission(Permission.IntegrationsManage)) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <p className="text-sm text-gray-500">
          You do not have permission to manage field mappings. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-6">
      {/* Authentication Check */}
      {isAuthLoading ? (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12">
          <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
          <p className="ml-3 text-sm text-gray-500">Checking authentication status...</p>
        </div>
      ) : !isAuthenticated ? (
        <Empty className="mb-32">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icon className="lucide--lock" />
            </EmptyMedia>
            <EmptyTitle>Relativity Authentication Required</EmptyTitle>
            <EmptyDescription>
              To configure field mappings, you need to authenticate with your Relativity account. This allows you to
              view available fields in your workspaces.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button variant="primary" onClick={connectRelativity} disabled={isConnecting}>
              {isConnecting ? (
                <>
                  <Icon className="lucide--loader-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Icon className="lucide--lock-open" />
                  Connect to Relativity
                </>
              )}
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col space-y-6">
          {/* Workspace Selector */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-fit space-y-2">
              <Label htmlFor="workspace" className="mb-3 block px-1">
                Select Relativity Workspace
              </Label>

              <Popover open={isWorkspacePickerOpen} onOpenChange={setIsWorkspacePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="workspace"
                    variant="outline"
                    role="combobox"
                    aria-expanded={isWorkspacePickerOpen}
                    className="w-full justify-between font-normal md:w-96"
                  >
                    <span className={cn('truncate', !selectedWorkspace && 'text-gray-500')}>
                      {selectedWorkspace
                        ? selectedWorkspace.name
                        : isLoadingWorkspaces
                          ? 'Loading workspaces...'
                          : 'Choose a workspace...'}
                    </span>
                    <Icon className="lucide--chevrons-up-down ml-2 size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0 md:w-96" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search workspaces..."
                      value={workspaceSearchQuery}
                      onValueChange={setWorkspaceSearchQuery}
                    />

                    <CommandList className="max-h-72">
                      {workspaceError ? (
                        <div className="px-3 py-4 text-sm text-red-700">
                          {workspaceError instanceof Error
                            ? workspaceError.message
                            : 'Failed to load Relativity workspaces'}
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>
                            {isLoadingWorkspaces
                              ? 'Loading workspaces...'
                              : workspaceSearchQuery
                                ? `No workspaces found matching "${workspaceSearchQuery}"`
                                : 'No workspaces found'}
                          </CommandEmpty>
                          <CommandGroup
                            className={cn(
                              'transition-opacity',
                              isFetchingWorkspaces && workspaces.length > 0 && 'opacity-50',
                            )}
                          >
                            {workspaces.map((workspace) => (
                              <CommandItem
                                key={workspace.artifactId}
                                value={`${workspace.name} ${workspace.artifactId}`}
                                className={cn(
                                  selectedWorkspace?.artifactId === workspace.artifactId
                                    ? 'bg-teal-50/50'
                                    : 'hover:bg-gray-50',
                                  'cursor-pointer px-2.5',
                                )}
                                onSelect={() => {
                                  setSelectedWorkspace(workspace);
                                  setSelectedField(null);
                                  setFieldSearchQuery('');
                                  resetWorkspaceSearch();
                                  setIsWorkspacePickerOpen(false);
                                }}
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                  <div>
                                    <div
                                      className={cn(
                                        selectedWorkspace?.artifactId === workspace.artifactId
                                          ? 'font-medium text-teal-500'
                                          : 'text-gray-800',
                                        'truncate text-sm',
                                      )}
                                    >
                                      {workspace.name}
                                    </div>
                                    <div
                                      className={cn(
                                        selectedWorkspace?.artifactId === workspace.artifactId
                                          ? 'text-teal-500'
                                          : 'text-gray-400',
                                        'text-xs',
                                      )}
                                    >
                                      ID: {workspace.artifactId}
                                    </div>
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>

                    {workspaceTotalPages > 1 && (
                      <div className="flex items-center justify-between border-t p-2 pl-4">
                        <span className="text-xs text-gray-500">
                          Page {workspaceCurrentPage} of {workspaceTotalPages}
                        </span>

                        <ButtonGroup>
                          <Button
                            type="button"
                            size={'iconSm'}
                            onClick={goToPreviousWorkspacePage}
                            disabled={!canPreviousWorkspacePage}
                          >
                            <Icon className="lucide--chevron-left size-4" />
                          </Button>
                          <Button
                            type="button"
                            size={'iconSm'}
                            onClick={goToNextWorkspacePage}
                            disabled={!canNextWorkspacePage}
                          >
                            <Icon className="lucide--chevron-right size-4" />
                          </Button>
                        </ButtonGroup>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Copy from another workspace */}
            {selectedWorkspace && !fieldsLoading && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button onClick={() => setIsCopyDialogOpen(true)} disabled={copySourceWorkspaces.length === 0}>
                      <Icon className="lucide--clipboard-paste" />
                      Copy mappings from another workspace
                    </Button>
                  </span>
                </TooltipTrigger>
                {copySourceWorkspaces.length === 0 && (
                  <TooltipContent>No other configured workspaces to copy from</TooltipContent>
                )}
              </Tooltip>
            )}
          </div>

          {/* Error Banner */}
          {selectedWorkspace && errorCount > 0 && (
            <div className="flex-none rounded-md border border-red-300 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <Icon className="lucide--x-circle mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-red-900">Mapping Errors</h4>
                  <p className="mt-1 text-sm text-red-700">
                    {errorCount} field{errorCount > 1 ? 's have' : ' has'} errors that must be fixed before saving.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Two-panel layout */}
          {selectedWorkspace && (
            <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row">
              {/* Left Panel - Field List */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:w-2/5">
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white">
                  <div className="flex-none border-b border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Relativity Fields</h3>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="show-mapped-only"
                          checked={showMappedOnly}
                          onCheckedChange={(checked) => setShowMappedOnly(checked === true)}
                        />
                        <label
                          htmlFor="show-mapped-only"
                          className="cursor-pointer text-sm text-gray-600 hover:text-gray-900"
                        >
                          Show Mapped Only
                        </label>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Input
                        placeholder="Search fields..."
                        value={fieldSearchQuery}
                        onChange={(e) => setFieldSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-2">
                    {fieldsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : filteredFields.length === 0 ? (
                      <div className="py-12 text-center">
                        <p className="text-sm text-gray-500">No fields found</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredFields.map((field) => {
                          const artifactId = field.artifactId.toString();
                          const isMapped = !!localMappings[artifactId];
                          const isSelected = selectedField?.artifactId === field.artifactId;
                          const validation = fieldValidations[artifactId];
                          const hasError = validation?.compatible === TypeCompatibility.Error;

                          return (
                            <button
                              key={field.artifactId}
                              onClick={() => setSelectedField(field)}
                              className={`w-full rounded-md p-3 px-4 text-left transition-colors ${
                                isSelected
                                  ? 'bg-teal-50 ring-1 ring-teal-500'
                                  : hasError
                                    ? 'border-l-2 border-red-400'
                                    : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-gray-900">
                                    {decodeHtmlEntities(field.name)}
                                  </p>
                                  <p className="text-xs text-gray-500">{field.fieldType}</p>
                                </div>
                                <div className="ml-2 flex flex-shrink-0 items-center gap-3">
                                  {field.isIdentifier === true && (
                                    <Badge className="border-purple-300 bg-purple-50 text-purple-500">Identifier</Badge>
                                  )}
                                  {hasError && <Icon className="lucide--x-circle h-4 w-4 text-red-500" />}
                                  {isMapped && !hasError && <div className="size-1.5 rounded-full bg-teal-500"></div>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Field Mapping */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:w-3/5">
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white">
                  <div className="flex-none border-b border-gray-200 p-4">
                    <h3 className="font-medium text-gray-900">Field Configuration</h3>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-6">
                    {!selectedField ? (
                      <div className="flex h-full flex-col items-center justify-center text-center">
                        <p className="text-sm text-gray-500">Select a field from the list to configure its mapping</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Label className="my-0 text-base font-semibold text-gray-900">
                              {decodeHtmlEntities(selectedField.name)}
                            </Label>
                            {selectedField.isIdentifier === true && (
                              <Badge className="border-purple-300 bg-purple-50 text-purple-500">Identifier</Badge>
                            )}
                          </div>
                          <p className="mt-2 text-sm text-gray-500">
                            Type: <span className="font-medium">{selectedField.fieldType}</span>
                          </p>
                        </div>

                        {/* Mapped Toggle - shown for non-object fields */}
                        {selectedField.fieldType !== 'Single Object' &&
                          selectedField.fieldType !== 'Multiple Object' &&
                          (() => {
                            const artifactId = selectedField.artifactId.toString();
                            const mapping = localMappings[artifactId];
                            const isMapped = mapping !== undefined;
                            const fieldKey = mapping?.fieldKey ?? generateFieldKey(selectedField.name);
                            const isOwnedByThisField = localParentIdentifierFieldKey === fieldKey;
                            const ownerEntry =
                              localParentIdentifierFieldKey !== null && !isOwnedByThisField
                                ? Object.values(localMappings).find((m) => m.fieldKey === localParentIdentifierFieldKey)
                                : undefined;
                            const isParentIdentifierDisabled = ownerEntry !== undefined;

                            return (
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <Label htmlFor="mapped-toggle" className="text-sm font-medium text-gray-900">
                                      Import this field
                                    </Label>
                                    <p className="text-xs text-gray-500">
                                      Enable to include this field when importing documents from Relativity
                                    </p>
                                  </div>
                                  <Switch
                                    id="mapped-toggle"
                                    checked={isMapped}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        enableFieldMapping(artifactId, selectedField);
                                      } else {
                                        clearFieldMapping(artifactId);
                                      }
                                    }}
                                  />
                                </div>

                                {isMapped && shouldShowParentIdentifierSwitch(selectedField) && (
                                  <div className="flex items-center justify-between border-gray-200 pt-4">
                                    <div className="pr-4">
                                      <Label htmlFor="parent-cn-toggle" className="text-sm font-medium text-gray-900">
                                        Parent Relativity Identifier
                                      </Label>
                                      <p className="text-xs text-gray-500">
                                        Treat this field's value as the Relativity Identifier of each document's parent
                                        file. Wexler will build parent/child relationships after import. Can only be
                                        enabled for one field per Workspace.
                                      </p>
                                    </div>
                                    {isParentIdentifierDisabled ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span>
                                            <Switch id="parent-cn-toggle" checked={false} disabled />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {`"${ownerEntry?.displayName ?? ownerEntry?.relativityFieldName}" is already configured as the parent identifier.`}
                                        </TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      <Switch
                                        id="parent-cn-toggle"
                                        checked={isOwnedByThisField}
                                        onCheckedChange={(checked) => {
                                          setLocalParentIdentifierFieldKey(checked ? fieldKey : null);
                                          setHasUnsavedChanges(true);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        {/* Available Options for Choice Fields */}
                        {isChoiceField && (
                          <div className="space-y-2">
                            <Label className="text-sm text-gray-700">Available Options</Label>
                            {(() => {
                              const mapping = localMappings[selectedField.artifactId.toString()];
                              const options = fieldChoicesData?.choices ?? mapping?.availableOptions;

                              if (isLoadingChoices) {
                                return (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
                                    <span>Loading options from Relativity...</span>
                                  </div>
                                );
                              }

                              if (!options || options.length === 0) {
                                return (
                                  <p className="text-sm text-gray-500 italic">No options available for this field.</p>
                                );
                              }

                              return (
                                <div className="max-h-32 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2">
                                  <div className="flex flex-wrap gap-1.5">
                                    {options.map((option) => (
                                      <span
                                        key={option}
                                        className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200"
                                      >
                                        {option}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* Object fields cannot be mapped */}
                        {(selectedField.fieldType === 'Single Object' ||
                          selectedField.fieldType === 'Multiple Object') && (
                          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3">
                            <Icon className="lucide--x-circle mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                            <p className="text-sm text-red-700">
                              Single Object and Multiple Object fields cannot be mapped. These fields reference other
                              Relativity objects.
                            </p>
                          </div>
                        )}

                        {/* Mapping configuration (shown when mapped) */}
                        {localMappings[selectedField.artifactId.toString()] && (
                          <>
                            {/* Display Name */}
                            <div className="my-4 space-y-2.5">
                              <Label htmlFor="display-name" className="mb-1.5">
                                Display Name
                              </Label>
                              <p className="text-xs text-gray-500">
                                This name will be shown in filters and column headers.
                              </p>
                              <Input
                                id="display-name"
                                value={localMappings[selectedField.artifactId.toString()]?.displayName || ''}
                                onChange={(e) => updateDisplayName(selectedField.artifactId.toString(), e.target.value)}
                                placeholder="Enter display name..."
                              />
                            </div>

                            {/* Is Array Toggle for Long Text fields */}
                            {shouldShowIsArrayCheckbox(selectedField) && (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="is-array"
                                    checked={isEffectivelyArray(localMappings[selectedField.artifactId.toString()])}
                                    onCheckedChange={(checked) =>
                                      updateIsArray(selectedField.artifactId.toString(), checked === true)
                                    }
                                  />
                                  <Label htmlFor="is-array" className="mb-0 cursor-pointer text-gray-800">
                                    Treat as array (split by separator)
                                  </Label>
                                </div>
                                <p className="text-xs text-gray-500">
                                  Enable if the field contains multiple values separated by a delimiter.
                                </p>
                              </div>
                            )}

                            {/* Array Separator */}
                            {shouldShowArraySeparator(
                              selectedField,
                              localMappings[selectedField.artifactId.toString()],
                            ) && (
                              <div className="space-y-2">
                                <Label htmlFor="array-separator">Array Separator</Label>
                                <Input
                                  id="array-separator"
                                  value={localMappings[selectedField.artifactId.toString()]?.arraySeparator ?? ';'}
                                  onChange={(e) =>
                                    updateArraySeparator(selectedField.artifactId.toString(), e.target.value)
                                  }
                                  placeholder=";"
                                  maxLength={5}
                                  className="w-32"
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save/Discard Actions */}
          {selectedWorkspace && (
            <div className="flex flex-none items-center justify-between border-gray-200 pb-6">
              <div className="ml-6 text-sm text-gray-500">
                {hasUnsavedChanges ? (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                    Unsaved changes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    All changes saved
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={handleDiscard} disabled={!hasUnsavedChanges}>
                  Discard Changes
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || hasValidationErrors || updateMappingMutation.isLoading}
                  title={hasValidationErrors ? `Cannot save: ${errorCount} field(s) have errors` : ''}
                >
                  <Icon className="lucide--save" />
                  {updateMappingMutation.isLoading ? 'Saving...' : 'Save Mappings'}
                </Button>
              </div>
            </div>
          )}

          <CopyFieldMappingsDialog
            open={isCopyDialogOpen}
            onOpenChange={setIsCopyDialogOpen}
            workspaces={copySourceWorkspaces}
            onCopy={handleCopyMappings}
          />
        </div>
      )}
    </div>
  );
}
